import React, { Component } from 'react';
import moment from 'moment';
import Loader from 'react-loader-spinner';
import DateRangePicker from 'rsuite/DateRangePicker';
import 'rsuite/dist/rsuite.min.css';
import SidebarComponent from '../SidebarComponent/SidebarComponent';
import ViewProfile from '../ViewProfile';
import AccessRestricted from '../AccessRestricted';
import {
    fetchInternationalTicketsDashboard,
    fetchInternationalTicketsList,
} from '../../actions/internationalTicketsAction';
import './InternationalTickets.css';

const { allowedRange } = DateRangePicker;

const COUNTRY_OPTIONS = [
    { value: '', label: 'All countries' },
    { value: 'india', label: 'India' },
    { value: 'uk', label: 'UK' },
    { value: 'us', label: 'US' },
    { value: 'uae', label: 'UAE' },
    { value: 'other', label: 'Other' },
];

const QUERY_BUCKET_LABELS = {
    order_related: 'Order Related Queries',
    product_warranty: 'Product Warranty Service',
    refund_related: 'Refund Related Queries',
    delivery_escalation: 'Delivery Escalations',
    other: 'Other Queries',
};

const COUNTRY_LABELS = {
    india: 'India',
    uk: 'UK',
    us: 'US',
    uae: 'UAE',
    other: 'Other Regions',
};

const PERIOD_LABELS = {
    daily: 'Daily',
    weekly: 'Last 7 Days',
    monthly: 'Month',
    custom: 'Selected Range',
};

const ALLOWED_ROLES = ['1', '2'];
const PAGE_SIZE = 20;

const truncate = (s, n = 60) => (!s ? '' : s.length > n ? s.slice(0, n - 1) + '…' : s);

class InternationalTickets extends Component {
    constructor(props) {
        super(props);
        this.state = {
            activeTab: 'dashboard',
            startDate: null,
            endDate: null,
            country: '',
            isLoading: false,
            data: null,
            error: null,

            search: '',
            listData: null,
            listPage: 1,
            listLoading: false,
            listError: null,
        };
    }

    async componentDidMount() {
        await this.loadDashboard();
    }

    loadDashboard = async () => {
        this.setState({ isLoading: true, error: null });
        const body = { country: this.state.country || undefined };
        if (this.state.startDate && this.state.endDate) {
            body.startDate = moment(this.state.startDate).format('YYYY-MM-DD');
            body.endDate = moment(this.state.endDate).format('YYYY-MM-DD');
        }
        const result = await fetchInternationalTicketsDashboard(body);
        if (result.success) {
            this.setState({ data: result.data, isLoading: false });
        } else {
            this.setState({ error: result.error, isLoading: false });
        }
    };

    loadList = async (page = 1) => {
        const search = (this.state.search || '').trim();
        this.setState({ listLoading: true, listError: null });
        const body = {
            country: this.state.country || undefined,
            search: search || undefined,
            page,
            limit: PAGE_SIZE,
        };
        if (this.state.startDate && this.state.endDate) {
            body.startDate = moment(this.state.startDate).format('YYYY-MM-DD');
            body.endDate = moment(this.state.endDate).format('YYYY-MM-DD');
        }
        const result = await fetchInternationalTicketsList(body);
        if (result.success) {
            this.setState({ listData: result.data, listPage: page, listLoading: false });
        } else {
            this.setState({ listError: result.error, listLoading: false });
        }
    };

    refreshActive = () => {
        if (this.state.activeTab === 'dashboard') {
            this.loadDashboard();
        } else {
            this.loadList(1);
        }
    };

    handleTabChange = (tab) => {
        if (tab === this.state.activeTab) return;
        this.setState({ activeTab: tab }, () => {
            if (tab === 'list' && !this.state.listData) {
                this.loadList(1);
            }
        });
    };

    handleDateSelect = (value) => {
        if (!value || value.length !== 2) {
            this.setState({ startDate: null, endDate: null }, this.refreshActive);
            return;
        }
        this.setState({ startDate: value[0], endDate: value[1] }, this.refreshActive);
    };

    handleClearDate = () => {
        this.setState({ startDate: null, endDate: null }, this.refreshActive);
    };

    handleCountryChange = (e) => {
        this.setState({ country: e.target.value }, this.refreshActive);
    };

    handleSearchChange = (e) => {
        this.setState({ search: e.target.value });
    };

    handleSearchSubmit = () => {
        this.loadList(1);
    };

    handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            this.handleSearchSubmit();
        }
    };

    handleClearSearch = () => {
        this.setState({ search: '' }, () => this.loadList(1));
    };

    handlePageChange = (newPage) => {
        if (newPage < 1) return;
        this.loadList(newPage);
    };

    renderOperationsSummary = (periods) => {
        const rows = [
            { key: 'total', label: 'Total Emails Received' },
            { key: 'pending', label: 'Pending Cases' },
            { key: 'resolved', label: 'Resolved Cases' },
            { key: 'sla_breached', label: 'SLA Breached Cases' },
            { key: 'avg_frt_hours', label: 'Average FRT (hrs)', isFloat: true },
        ];
        return (
            <div className="it-section">
                <h4>Operations Summary</h4>
                <table className="it-table">
                    <thead>
                        <tr>
                            <th>KPI / Parameter</th>
                            {periods.map((p) => (
                                <th key={p.label}>{PERIOD_LABELS[p.label] || p.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.key}>
                                <td>{r.label}</td>
                                {periods.map((p) => {
                                    const val = p.operations_summary && p.operations_summary[r.key];
                                    return (
                                        <td key={p.label}>
                                            {val == null ? '-' : r.isFloat ? Number(val).toFixed(2) : val}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    renderQueryBucket = (periods) => {
        const buckets = Object.keys(QUERY_BUCKET_LABELS);
        return (
            <div className="it-section">
                <h4>Query Bucket Overview</h4>
                <table className="it-table">
                    <thead>
                        <tr>
                            <th>KPI / Parameter</th>
                            {periods.map((p) => (
                                <th key={p.label}>{PERIOD_LABELS[p.label] || p.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {buckets.map((bucket) => (
                            <tr key={bucket}>
                                <td>{QUERY_BUCKET_LABELS[bucket]}</td>
                                {periods.map((p) => {
                                    const entry =
                                        p.query_bucket_overview &&
                                        p.query_bucket_overview.find((b) => b.query_bucket === bucket);
                                    return <td key={p.label}>{entry ? entry.count : 0}</td>;
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    renderCountryWise = (periods) => {
        const countries = Object.keys(COUNTRY_LABELS);
        return (
            <div className="it-section">
                <h4>Country-Wise Support Volume</h4>
                <table className="it-table">
                    <thead>
                        <tr>
                            <th>Country</th>
                            {periods.map((p) => (
                                <th key={p.label}>{PERIOD_LABELS[p.label] || p.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {countries.map((c) => (
                            <tr key={c}>
                                <td>{COUNTRY_LABELS[c]}</td>
                                {periods.map((p) => {
                                    const entry =
                                        p.country_wise_support_volume &&
                                        p.country_wise_support_volume.find((b) => b.country === c);
                                    return <td key={p.label}>{entry ? entry.count : 0}</td>;
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    renderSlaFrt = (periods) => {
        const rows = [
            { key: 'sla_compliance_pct', label: 'SLA Compliance', suffix: '%' },
            { key: 'frt_within_8h_pct', label: 'FRT Within 8 Hours', suffix: '%' },
        ];
        return (
            <div className="it-section">
                <h4>SLA &amp; FRT Monitoring</h4>
                <table className="it-table">
                    <thead>
                        <tr>
                            <th>KPI / Parameter</th>
                            {periods.map((p) => (
                                <th key={p.label}>{PERIOD_LABELS[p.label] || p.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.key}>
                                <td>{r.label}</td>
                                {periods.map((p) => {
                                    const val = p.sla_frt_monitoring && p.sla_frt_monitoring[r.key];
                                    return (
                                        <td key={p.label}>
                                            {val == null ? '-' : `${val}${r.suffix}`}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    renderEscalationAging = (periods) => {
        const rows = [
            { key: '0_8_hours', label: '0–8 Hours' },
            { key: '8_24_hours', label: '8–24 Hours' },
            { key: '24_48_hours', label: '24–48 Hours' },
            { key: '48_plus_hours', label: '48+ Hours' },
        ];
        return (
            <div className="it-section">
                <h4>Escalation Aging</h4>
                <table className="it-table">
                    <thead>
                        <tr>
                            <th>Bucket</th>
                            {periods.map((p) => (
                                <th key={p.label}>{PERIOD_LABELS[p.label] || p.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.key}>
                                <td>{r.label}</td>
                                {periods.map((p) => {
                                    const val = p.escalation_aging && p.escalation_aging[r.key];
                                    return <td key={p.label}>{val == null ? 0 : val}</td>;
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    renderListTab = () => {
        const { search, listData, listLoading, listError, listPage } = this.state;
        const rows = (listData && listData.rows) || [];
        const total = (listData && listData.total) || 0;
        const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
        const showingFrom = total === 0 ? 0 : (listPage - 1) * PAGE_SIZE + 1;
        const showingTo = Math.min(total, listPage * PAGE_SIZE);

        return (
            <div className="it-list-wrap">
                <div className="it-list-toolbar">
                    <div className="it-email-search">
                        <input
                            type="text"
                            className="it-email-input"
                            placeholder="Search by name or email (full email = exact match)"
                            value={search}
                            onChange={this.handleSearchChange}
                            onKeyDown={this.handleSearchKeyDown}
                        />
                        <button className="it-btn" onClick={this.handleSearchSubmit}>
                            Search
                        </button>
                        {search && (
                            <button className="it-btn it-btn-secondary" onClick={this.handleClearSearch}>
                                Clear
                            </button>
                        )}
                    </div>
                </div>

                {listLoading ? (
                    <div className="loader">
                        <Loader type="Oval" color="Grey" height={120} width={120} />
                    </div>
                ) : listError ? (
                    <div className="it-error">{listError}</div>
                ) : (
                    <div className="it-section">
                        <table className="it-table it-list-table">
                            <thead>
                                <tr>
                                    <th>Received At</th>
                                    <th>Customer Name</th>
                                    <th>Customer Email</th>
                                    <th>Subject</th>
                                    <th>Country</th>
                                    <th>Bucket</th>
                                    <th>Status</th>
                                    <th>SLA</th>
                                    <th>FRT (hrs)</th>
                                    <th>Msgs</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 ? (
                                    <tr>
                                        <td colSpan="10" className="it-empty">
                                            No tickets found for the selected filters.
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map((row) => (
                                        <tr key={row.thread_id}>
                                            <td>{row.received_at ? moment(row.received_at).format('YYYY-MM-DD HH:mm') : '-'}</td>
                                            <td>{row.customer_name || '-'}</td>
                                            <td>{row.customer_email || '-'}</td>
                                            <td title={row.subject || ''}>{truncate(row.subject, 60)}</td>
                                            <td>{COUNTRY_LABELS[row.country] || row.country || '-'}</td>
                                            <td>{QUERY_BUCKET_LABELS[row.query_bucket] || row.query_bucket || '-'}</td>
                                            <td>
                                                <span className={`it-status it-status-${row.status}`}>
                                                    {row.status}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={row.sla_breached ? 'it-sla-breached' : ''}>
                                                    {row.sla_breached ? 'BREACHED' : 'OK'}
                                                </span>
                                            </td>
                                            <td>{row.frt_hours == null ? '-' : Number(row.frt_hours).toFixed(2)}</td>
                                            <td>{row.message_count}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>

                        <div className="it-pagination">
                            <span className="it-pagination-info">
                                {total > 0
                                    ? `Showing ${showingFrom}–${showingTo} of ${total}`
                                    : 'Showing 0 of 0'}
                            </span>
                            <div className="it-pagination-controls">
                                <button
                                    className="it-btn it-btn-secondary"
                                    disabled={listPage <= 1}
                                    onClick={() => this.handlePageChange(listPage - 1)}
                                >
                                    Previous
                                </button>
                                <span className="it-pagination-page">
                                    Page {listPage} of {totalPages}
                                </span>
                                <button
                                    className="it-btn it-btn-secondary"
                                    disabled={listPage >= totalPages}
                                    onClick={() => this.handlePageChange(listPage + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    render() {
        const userRoleId = localStorage.getItem('userRoleId');
        if (!ALLOWED_ROLES.includes(userRoleId)) {
            return <AccessRestricted />;
        }

        const { activeTab, data, isLoading, error, startDate, endDate, country } = this.state;
        const periods = (data && data.periods) || [];

        return (
            <div>
                {window.innerWidth > 600 && <SidebarComponent sideProp={'IT'} />}
                <ViewProfile />

                <div className="content-dashboard">
                    <div className="content-title">
                        <h3>International Tickets</h3>

                        <div className="it-filters">
                            <div className="it-filter-item">
                                <DateRangePicker
                                    showOneCalendar
                                    format="dd/MM/yyyy"
                                    placeholder="Select date range"
                                    placement="bottomEnd"
                                    disabledDate={allowedRange(
                                        moment(new Date(new Date().setDate(new Date().getDate() - 365))).format('YYYY-MM-DD'),
                                        moment(new Date()).format('YYYY-MM-DD')
                                    )}
                                    value={startDate && endDate ? [new Date(startDate), new Date(endDate)] : null}
                                    onOk={this.handleDateSelect}
                                    onClean={this.handleClearDate}
                                />
                            </div>

                            <div className="it-filter-item">
                                <select
                                    className="it-country-select"
                                    value={country}
                                    onChange={this.handleCountryChange}
                                >
                                    {COUNTRY_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="it-tabs">
                        <button
                            className={`it-tab ${activeTab === 'dashboard' ? 'it-tab--active' : ''}`}
                            onClick={() => this.handleTabChange('dashboard')}
                        >
                            Dashboard
                        </button>
                        <button
                            className={`it-tab ${activeTab === 'list' ? 'it-tab--active' : ''}`}
                            onClick={() => this.handleTabChange('list')}
                        >
                            Tickets List
                        </button>
                    </div>

                    {activeTab === 'dashboard' ? (
                        isLoading ? (
                            <div className="loader">
                                <Loader type="Oval" color="Grey" height={200} width={200} />
                            </div>
                        ) : error ? (
                            <div className="it-error">{error}</div>
                        ) : (
                            <div className="it-dashboard">
                                {this.renderOperationsSummary(periods)}
                                {this.renderQueryBucket(periods)}
                                {this.renderCountryWise(periods)}
                                {this.renderSlaFrt(periods)}
                                {this.renderEscalationAging(periods)}
                            </div>
                        )
                    ) : (
                        this.renderListTab()
                    )}
                </div>
            </div>
        );
    }
}

export default InternationalTickets;
